package com.daytimegaming.ourdailyfinances.data.repository

import com.daytimegaming.ourdailyfinances.data.network.dto.BudgetDto
import com.daytimegaming.ourdailyfinances.data.network.dto.FixedIncomeDto
import com.daytimegaming.ourdailyfinances.data.network.dto.HoldingDto
import com.daytimegaming.ourdailyfinances.data.network.dto.InvestmentTransactionDto
import com.daytimegaming.ourdailyfinances.data.network.dto.InvestmentsDto
import com.daytimegaming.ourdailyfinances.data.network.dto.OptionContractDto
import com.daytimegaming.ourdailyfinances.data.network.dto.SecurityDto
import com.daytimegaming.ourdailyfinances.data.network.dto.TransactionDto
import com.daytimegaming.ourdailyfinances.data.network.dto.YieldRateDto
import com.daytimegaming.ourdailyfinances.domain.model.Budget
import com.daytimegaming.ourdailyfinances.domain.model.Counterparty
import com.daytimegaming.ourdailyfinances.domain.model.FixedIncome
import com.daytimegaming.ourdailyfinances.domain.model.Holding
import com.daytimegaming.ourdailyfinances.domain.model.InvestmentTransaction
import com.daytimegaming.ourdailyfinances.domain.model.Investments
import com.daytimegaming.ourdailyfinances.domain.model.Location
import com.daytimegaming.ourdailyfinances.domain.model.OptionContract
import com.daytimegaming.ourdailyfinances.domain.model.PaymentMeta
import com.daytimegaming.ourdailyfinances.domain.model.PersonalFinanceCategory
import com.daytimegaming.ourdailyfinances.domain.model.Security
import com.daytimegaming.ourdailyfinances.domain.model.Transaction
import com.daytimegaming.ourdailyfinances.domain.model.YieldRate

fun TransactionDto.toDomain(): Transaction = Transaction(
    transactionId = transactionId,
    accountId = accountId,
    amount = amount,
    date = date,
    name = name,
    merchantName = merchantName,
    category = (userCategoryOverride ?: personalFinanceCategory)?.primary?.let { listOf(it) } ?: category,
    pending = pending,
    isoCurrencyCode = isoCurrencyCode,
    logoUrl = logoUrl,
    website = website,
    personalFinanceCategoryIconUrl = personalFinanceCategoryIconUrl,
    counterparties = counterparties?.map {
        Counterparty(
            name = it.name,
            type = it.type,
            website = it.website,
            logoUrl = it.logoUrl,
            entityId = it.entityId,
            confidenceLevel = it.confidenceLevel
        )
    },
    location = location?.let {
        Location(
            address = it.address,
            city = it.city,
            region = it.region,
            postalCode = it.postalCode,
            country = it.country,
            lat = it.lat,
            lon = it.lon,
            storeNumber = it.storeNumber
        )
    },
    paymentMeta = paymentMeta?.let {
        PaymentMeta(
            referenceNumber = it.referenceNumber,
            ppdId = it.ppdId,
            payee = it.payee,
            byOrderOf = it.byOrderOf,
            payer = it.payer,
            paymentMethod = it.paymentMethod,
            paymentProcessor = it.paymentProcessor,
            reason = it.reason
        )
    },
    personalFinanceCategory = personalFinanceCategory?.let {
        PersonalFinanceCategory(
            primary = it.primary,
            detailed = it.detailed,
            confidenceLevel = it.confidenceLevel,
            version = it.version
        )
    },
    userCategoryOverride = userCategoryOverride?.let {
        PersonalFinanceCategory(
            primary = it.primary,
            detailed = it.detailed,
            confidenceLevel = it.confidenceLevel,
            version = it.version
        )
    }
)

fun com.daytimegaming.ourdailyfinances.data.network.dto.RecurringAmountDto.toDomain(): com.daytimegaming.ourdailyfinances.domain.model.RecurringAmount =
    com.daytimegaming.ourdailyfinances.domain.model.RecurringAmount(
        amount = amount,
        isoCurrencyCode = isoCurrencyCode
    )

fun com.daytimegaming.ourdailyfinances.data.network.dto.RecurringTransactionStreamDto.toDomain(): com.daytimegaming.ourdailyfinances.domain.model.RecurringTransactionStream =
    com.daytimegaming.ourdailyfinances.domain.model.RecurringTransactionStream(
        streamId = streamId,
        accountId = accountId,
        streamType = streamType,
        description = description,
        merchantName = merchantName,
        firstDate = firstDate,
        lastDate = lastDate,
        predictedNextDate = predictedNextDate,
        frequency = frequency,
        transactionIds = transactionIds,
        averageAmount = averageAmount.toDomain(),
        lastAmount = lastAmount.toDomain(),
        isActive = isActive,
        status = status,
        personalFinanceCategory = personalFinanceCategory?.let {
            PersonalFinanceCategory(
                primary = it.primary,
                detailed = it.detailed,
                confidenceLevel = it.confidenceLevel,
                version = it.version
            )
        }
    )

fun BudgetDto.toDomain(): Budget = Budget(
    budgetId = budgetId,
    name = name,
    categories = categories,
    targetValue = targetValue,
    createdAt = createdAt,
    lastUpdatedAt = lastUpdatedAt,
    lastUpdatedByUserId = lastUpdatedByUserId,
    createdByUserId = createdByUserId,
    dashboardIds = dashboardIds
)

fun OptionContractDto.toDomain(): OptionContract = OptionContract(
    contractType = contractType,
    expirationDate = expirationDate,
    strikePrice = strikePrice,
    underlyingSecurityTicker = underlyingSecurityTicker
)

fun YieldRateDto.toDomain(): YieldRate = YieldRate(
    percentage = percentage,
    type = type
)

fun FixedIncomeDto.toDomain(): FixedIncome = FixedIncome(
    maturityDate = maturityDate,
    issueDate = issueDate,
    faceValue = faceValue,
    yieldRate = yieldRate?.toDomain()
)

fun SecurityDto.toDomain(): Security = Security(
    securityId = securityId,
    name = name,
    tickerSymbol = tickerSymbol,
    type = type,
    subtype = subtype,
    isin = isin,
    cusip = cusip,
    sedol = sedol,
    institutionSecurityId = institutionSecurityId,
    institutionId = institutionId,
    proxySecurityId = proxySecurityId,
    isCashEquivalent = isCashEquivalent,
    closePrice = closePrice,
    closePriceAsOf = closePriceAsOf,
    isoCurrencyCode = isoCurrencyCode,
    unofficialCurrencyCode = unofficialCurrencyCode,
    marketIdentifierCode = marketIdentifierCode,
    sector = sector,
    industry = industry,
    cfiCode = cfiCode,
    updateDatetime = updateDatetime,
    optionContract = optionContract?.toDomain(),
    fixedIncome = fixedIncome?.toDomain()
)

fun HoldingDto.toDomain(): Holding = Holding(
    accountId = accountId,
    securityId = securityId,
    security = security?.toDomain(),
    proxySecurity = proxySecurity?.toDomain(),
    institutionPrice = institutionPrice,
    institutionPriceAsOf = institutionPriceAsOf,
    institutionValue = institutionValue,
    costBasis = costBasis,
    quantity = quantity,
    isoCurrencyCode = isoCurrencyCode
)

fun InvestmentTransactionDto.toDomain(): InvestmentTransaction = InvestmentTransaction(
    investmentTransactionId = investmentTransactionId,
    accountId = accountId,
    securityId = securityId,
    security = security?.toDomain(),
    date = date,
    name = name,
    quantity = quantity,
    amount = amount,
    price = price,
    fees = fees,
    type = type,
    subtype = subtype,
    isoCurrencyCode = isoCurrencyCode,
    cancelTransactionId = cancelTransactionId
)

fun InvestmentsDto.toDomain(): Investments = Investments(
    holdings = holdings.map { it.toDomain() },
    transactions = transactions.map { it.toDomain() }
)

fun com.daytimegaming.ourdailyfinances.data.network.dto.PortfolioHistoryEntryDto.toDomain(): com.daytimegaming.ourdailyfinances.domain.model.PortfolioHistoryEntry =
    com.daytimegaming.ourdailyfinances.domain.model.PortfolioHistoryEntry(
        date = date,
        value = value
    )

fun com.daytimegaming.ourdailyfinances.data.network.dto.PortfolioHistoryResponse.toDomain(): com.daytimegaming.ourdailyfinances.domain.model.PortfolioHistory =
    com.daytimegaming.ourdailyfinances.domain.model.PortfolioHistory(
        history = history.map { it.toDomain() }
    )

fun com.daytimegaming.ourdailyfinances.data.network.dto.SecurityPerformanceResponse.toDomain(): com.daytimegaming.ourdailyfinances.domain.model.SecurityPerformance =
    com.daytimegaming.ourdailyfinances.domain.model.SecurityPerformance(
        history = history.mapValues { (_, entries) -> entries.map { it.toDomain() } }
    )

fun com.daytimegaming.ourdailyfinances.data.network.dto.RealEstateValueEstimateDto.toDomain(): com.daytimegaming.ourdailyfinances.domain.model.RealEstateValueEstimate =
    com.daytimegaming.ourdailyfinances.domain.model.RealEstateValueEstimate(
        date = date,
        estimatedValue = estimatedValue,
        priceRangeLow = priceRangeLow,
        priceRangeHigh = priceRangeHigh
    )

fun com.daytimegaming.ourdailyfinances.data.network.dto.RealEstateHoldingDto.toDomain(): com.daytimegaming.ourdailyfinances.domain.model.RealEstateHolding =
    com.daytimegaming.ourdailyfinances.domain.model.RealEstateHolding(
        realEstateHoldingId = realEstateHoldingId,
        userId = userId,
        nickname = nickname,
        addressLine1 = addressLine1,
        addressLine2 = addressLine2,
        city = city,
        state = state,
        zipCode = zipCode,
        county = county,
        purchasePrice = purchasePrice,
        mortgageAmount = mortgageAmount,
        mortgageInterestRate = mortgageInterestRate,
        createdAt = createdAt,
        addedByUserId = addedByUserId,
        valueEstimates = valueEstimates.map { it.toDomain() }
    )

fun com.daytimegaming.ourdailyfinances.data.network.dto.PlaidPricesSyncResponse.toDomain(): com.daytimegaming.ourdailyfinances.domain.model.PlaidPricesSync =
    com.daytimegaming.ourdailyfinances.domain.model.PlaidPricesSync(
        jobId = jobId,
        tickers = tickers,
        source = source,
        status = status
    )

